'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "messageId", "index", "items", "groupKey", "slots", "slotProps"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useMessage, useMessageIds } from "../hooks/useMessage.js";
import { useChatVariant } from "../chat/internals/ChatVariantContext.js";
import { useChatDensity } from "../chat/internals/ChatDensityContext.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { MessageAvatar } from "../message/MessageAvatar.js";
import { MessageContent } from "../message/MessageContent.js";
import { MessageMeta } from "../message/MessageMeta.js";
import { MessageRoot } from "../message/MessageRoot.js";

/**
 * A function that maps a message to a group key.
 * Messages that resolve to the same key are visually grouped together
 * (shared avatar, author name, etc.).
 * @param {ChatMessage} message The message to derive a group key from.
 * @returns {string | number} The group key for the message.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_GROUP_KEY = message => message.author?.id ?? message.role ?? '';

/**
 * Creates a `groupKey` function that groups messages by author within a sliding
 * time window. Messages from the same author sent more than `windowMs` milliseconds
 * apart will start a new group.
 *
 * @param windowMs - The grouping window in milliseconds. Defaults to 300 000 (5 minutes).
 *
 * @example
 * // Group messages from the same author within a 1-minute window
 * <MessageGroup groupKey={createTimeWindowGroupKey(60_000)} messageId={id} />
 */
export function createTimeWindowGroupKey(windowMs = 300_000) {
  return message => {
    const timestamp = message.createdAt ? Date.parse(message.createdAt) : null;
    const bucket = timestamp != null && !Number.isNaN(timestamp) ? Math.floor(timestamp / windowMs) : 0;
    return `${message.author?.id ?? message.role ?? ''}-${bucket}`;
  };
}
function resolveMessageIndex(messageId, index, items) {
  if (index != null) {
    return index;
  }
  return items.indexOf(messageId);
}
function getAuthorLabel(message) {
  if (!message) {
    return null;
  }
  return message.author?.displayName ?? message.author?.id ?? message.role;
}
export const MessageGroup = /*#__PURE__*/React.forwardRef(function MessageGroup(props, ref) {
  const {
      children,
      messageId,
      index,
      items: itemsProp,
      groupKey = DEFAULT_GROUP_KEY,
      slots,
      slotProps
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const defaultItems = useMessageIds();
  const items = itemsProp ?? defaultItems;
  const messageIndex = resolveMessageIndex(messageId, index, items);
  const previousMessageId = messageIndex > 0 ? items[messageIndex - 1] : undefined;
  const nextMessageId = messageIndex >= 0 && messageIndex < items.length - 1 ? items[messageIndex + 1] : undefined;
  const message = useMessage(messageId);
  const previousMessage = useMessage(previousMessageId ?? '');
  const nextMessage = useMessage(nextMessageId ?? '');
  const variant = useChatVariant();
  const density = useChatDensity();
  const prevKey = previousMessage ? groupKey(previousMessage) : null;
  const currentKey = message ? groupKey(message) : null;
  const nextKey = nextMessage ? groupKey(nextMessage) : null;
  const isFirst = prevKey === null || prevKey !== currentKey;
  const isFirstInList = messageIndex === 0;
  const isLast = nextKey === null || nextKey !== currentKey;
  const ownerState = React.useMemo(() => ({
    isFirst,
    isFirstInList,
    isLast,
    authorRole: message?.role,
    authorId: message?.author?.id,
    variant,
    density
  }), [density, isFirst, isFirstInList, isLast, message?.author?.id, message?.role, variant]);
  const Group = slots?.group ?? 'div';
  const AuthorName = slots?.authorName ?? 'div';
  const groupProps = useSlotProps({
    elementType: Group,
    externalSlotProps: slotProps?.group,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref
    }, getDataAttributes({
      isFirst: ownerState.isFirst,
      isLast: ownerState.isLast,
      authorRole: ownerState.authorRole
    }))
  });
  const authorNameProps = useSlotProps({
    elementType: AuthorName,
    externalSlotProps: slotProps?.authorName,
    ownerState
  });
  const authorLabel = getAuthorLabel(message);
  const showGroupAuthorName = isFirst && Boolean(authorLabel);
  const authorNameElement = showGroupAuthorName ? /*#__PURE__*/_jsx(AuthorName, _extends({}, authorNameProps, {
    children: authorLabel
  })) : null;

  // In compact mode the author name lives inside the message grid so it shares
  // a row with the avatar. In default mode it sits above the grid.
  const compactAuthorName = variant === 'compact' ? authorNameElement : null;
  const defaultAuthorName = variant !== 'compact' ? authorNameElement : null;
  return /*#__PURE__*/_jsxs(Group, _extends({}, groupProps, {
    children: [defaultAuthorName, children ?
    // When custom children are provided (e.g. from DefaultMessageItem),
    // pass `isGrouped` via cloneElement so the inner MessageRoot/ChatMessage
    // receives the correct grouping state for its context.
    // In compact mode, also inject the author name element into the
    // children so it appears inside the CSS grid (sharing a row with the avatar).
    // We wrap in a Fragment to avoid duplicate-key warnings.
    React.Children.map(children, child => {
      if (! /*#__PURE__*/React.isValidElement(child) || typeof child.type === 'string') {
        return child;
      }
      const clone = child;
      if (compactAuthorName) {
        const existingChildren = clone.props.children;
        return /*#__PURE__*/React.cloneElement(clone, {
          isGrouped: !isFirst,
          children: /*#__PURE__*/_jsxs(React.Fragment, {
            children: [compactAuthorName, existingChildren]
          })
        });
      }
      return /*#__PURE__*/React.cloneElement(clone, {
        isGrouped: !isFirst
      });
    }) : /*#__PURE__*/_jsxs(MessageRoot, {
      isGrouped: !isFirst,
      messageId: messageId,
      children: [/*#__PURE__*/_jsx(MessageAvatar, {}), compactAuthorName, /*#__PURE__*/_jsx(MessageContent, {}), /*#__PURE__*/_jsx(MessageMeta, {})]
    })]
  }));
});
if (process.env.NODE_ENV !== "production") MessageGroup.displayName = "MessageGroup";